<script setup lang="ts">
import { parseErrors } from "@/assets/js/parseErrors";
import { useToastStore } from "@/store/toast";
import { useExpenseTemp } from "@/store/expensesTemp";
import api from "@/api";
import dayjs from "dayjs";
import { useDefaultStore } from "@/store";

// const props = defineProps({
// 	day: {
// 		type: Object,
// 		default: () => ({}),
// 	},
// 	classNameFilter: Function,
// 	getProp: Function,
// 	getShifts: Function,
// 	tempId: {
// 		type: [Number, null],
// 		default: null,
// 		validator: (prop: any) => typeof prop === "number" || prop === null,
// 	},
// });

const store = useDefaultStore();
const expenseTempStore = useExpenseTemp();
const toastStore = useToastStore();

const props = withDefaults(
	defineProps<{
		id: any;
	}>(),
	{},
);
const emit = defineEmits(["close-modal", "confirm"]);
const loading = ref(false);
const monetarySymbol = computed(() => {
	const iStatus = store.initialStatus;
	return iStatus?.monetary_symbol;
});

const data = ref<any>({});
const receipt = ref<any>();
const loadingFile = ref(false);
const loadingData = ref(false);

async function getExpenseById() {
	loadingData.value = true;
	try {
		if (!props.id) throw new Error("No expense id");
		const result = await api.getExpenseById(props.id);
		const res = result.data.data;
		data.value = {
			old_value: res.old_value ? Math.round(res.old_value * 100) / 100 : null,
			id: res.id,
			status: res.status,
			rejectreason: res.rejectreason,
			client: res.client?.name,
			expenseLocation: res.expenseLocation?.name,
			category: res.subcategory?.name,
			distance: res.distance,
			expenseType: {
				id: res.expenseType.id,
				value: res.expenseType.label.replace("Push Bike", "Bicycle"),
				code: res.expenseType.type,
			},
			engineType: res.engine_type,
			amount: Math.round(res.value * 100) / 100,
			travelFrom: res.locationfrom,
			travelTo: res.locationto,
			location: res.location,
			description: res.usp_description,
			reason: res.reason,
		};
		if (res.startTime && res.endTime) {
			data.value.startDate = dayjs.utc(res.startTime).format("DD-MM-YYYY");
			data.value.endDate = dayjs.utc(res.endTime).format("DD-MM-YYYY");
			data.value.startTime = dayjs.utc(res.startTime).format("HH:mm");
			data.value.endTime = dayjs.utc(res.endTime).format("HH:mm");
		}
		if (res.startTime && !res.endTime)
			data.value.date = dayjs.utc(res.startTime).format("DD-MM-YYYY");
		if (res.files.length) await prepareReceipt(res);
	} catch (error) {
		console.log(error);
	} finally {
		loadingData.value = false;
	}
}
async function prepareReceipt(result) {
	loadingFile.value = true;
	const file = result.files[0];
	try {
		const imgSrc = await expenseTempStore.fetchImage(file.id);
		receipt.value = {
			title: "Receipt",
			file: imgSrc,
		};
	} catch (err) {
		console.log(err.response);
		const errs = parseErrors(err);
		toastStore.openToastError(errs ?? " Unable to read file");
	}

	loadingFile.value = false;
}
async function cancelClaim() {
	loading.value = true;
	try {
		const res = await api.deleteExpense(data.value.id);
		console.log(res.data);
		toastStore.openToastSuccess("You have successfully canceled the claim.");
		emit("confirm");
		emit("close-modal");
	} catch (err) {
		const errs = parseErrors(err, "Error, canceled the claim", true);
		toastStore.openToastError(errs);
	} finally {
		loading.value = false;
	}
}

onMounted(() => {
	getExpenseById();
});

function formatKey(key) {
	const keyMapper = {
		old_value: "Provisionally calculated value",
		id: "Expense ID",
		client: "Client",
		expenseLocation: "Ward", // ward
		category: "Grade", // grade
		distance: "Distance",
		vehicleType: "Vehicle Type",
		engineType: "Engine Type",
		time: "Time",
		date: "Date",
		startDate: "Start Date",
		endDate: "End Date",
		startTime: "Start Time",
		endTime: "End Time",
		travelFrom: "Travel From",
		travelTo: "Travel To",
		locationOfSubsistence: "Location of Subsistence",
		location: "Location of Subsistence",
		file: "File",
		band: "Band",
		reason: "Reason",
		note: "Note",
		description: "Description",
		rejectreason: "Reason",
		expenseType: "Expense Type",
	};
	return keyMapper[key];
}
function getValue(item, key) {
	// if (key.match(/time/i)) {
	//   return moment(item, 'HH:mm:ss').format('HH:mm')
	// }
	if (!item) return null;
	if (typeof item === "object") return item.value || item.name;
	if (key === "distance") return item + " km";
	if (key === "old_value") return this.monet + item;
	if (key === "id") return "#" + item;
	return item;
}
const allDataItems = computed(() => {
	const arr = [];
	if (!data.value) return arr;

	for (const key of Object.keys(data.value)) {
		if (key === "rejectreason") console.log(key, data.value[key]);
		if (data.value[key] == null || data.value[key] == undefined) continue;
		if (!formatKey(key)) continue;
		if (getValue(data.value[key], key) === "Invalid date") continue;
		const item = {
			title: formatKey(key),
			value: getValue(data.value[key], key),
		};
		arr.push(item);
	}
	return arr;
});

const renderAsterisk = computed(() => {
	if (!data.value) return "";
	if (
		data.value.expenseType?.code &&
		data.value.status === store.expenseStatus.PENDING
	)
		return "*";
	return "";
});
const amountLabel = computed(() => {
	if (!data.value) return "Amount";
	if (data.value.status === store.expenseStatus.REJECTED)
		return "Rejected amount";
	if (data.value.status === store.expenseStatus.APPROVED)
		return "Approved amount";
	return "Amount";
});
const statusDisplay = computed(() => {
	switch (data.value.status) {
		case store.expenseStatus.APPROVED: {
			return { approved: true };
		}
		case store.expenseStatus.REJECTED: {
			return { rejected: true };
		}
		case store.expenseStatus.CANCELLED: {
			return { cancelled: true };
		}
		case store.expenseStatus.PENDING: {
			return { awaiting: true };
		}

		default: {
			break;
		}
	}
});
const canCancel = computed(() => {
	if (data.value) {
		return data.value.status === store.expenseStatus.PENDING;
	}
	return false;
});

// cancel expense
const isCancel = ref(false);

function returnF() {
	if (isCancel.value) {
		isCancel.value = false;
	} else {
		emit("close-modal");
	}
}
</script>

<template>
	<div
		v-if="!loadingData && Object.keys(data).length > 0"
		class="expense-container"
	>
		<div class="header">
			<div class="preview">
				<div class="top title-color">
					<div class="monet">{{ monetarySymbol }}</div>
					<div
						v-if="data"
						class="amount"
					>
						{{ data.amount }}{{ renderAsterisk }}
					</div>
				</div>
				<div class="bottom">{{ amountLabel }}</div>
			</div>

			<div
				v-if="renderAsterisk"
				class="tooltipy title-color"
			>
				* Please note that the amount displayed above is a provisional
				calculation which may update upon approval.
			</div>
			<div
				v-if="data"
				class="status-wrap"
			>
				<div class="status">Status:</div>
				<div :class="statusDisplay"></div>
			</div>
		</div>
		<div class="content">
			<div
				v-for="(item, index) in allDataItems"
				:key="index"
				class="group"
			>
				<label class="label">{{ item.title }}</label>
				<div class="value">
					{{ item.value }}
				</div>
			</div>
		</div>

		<div
			v-if="loadingFile"
			class="loading-wrapper"
		>
			<img src="@/assets/image/loader.svg" />
		</div>
		<receiptRender
			v-if="receipt"
			:receipt="receipt"
		/>

		<div class="buttons-centered">
			<span
				v-if="isCancel"
				class="error"
			>
				Are you sure you want to cancel this claim?
			</span>
			<button
				v-if="canCancel && !isCancel"
				class="button alt is-generic-app-blue"
				:disabled="loading"
				@click.prevent="isCancel = true"
			>
				Cancel the claim
			</button>
			<button
				v-else-if="canCancel && isCancel"
				class="button alt is-generic-app-blue danger"
				:disabled="loading"
				@click.prevent="cancelClaim"
			>
				Yes, cancel the claim
			</button>

			<button
				class="button is-outlined"
				:disabled="loading"
				@click.prevent="returnF"
			>
				{{ isCancel ? "No" : "Return" }}
			</button>
		</div>
	</div>
	<div
		v-else
		class="expense-container"
	>
		<div class="loading-wrapper">
			<img src="@/assets/image/loader.svg" />
		</div>
	</div>
</template>
<style lang="scss" scoped>
$color-light-gray: #dde6eb;
$color-fa: #7c91ae;
$background-color-price: #e7edf4cc;

.expense-container {
	width: 589px;
	// overflow: hidden;
	height: calc(100vh - 150px);
	overflow-y: auto;
	box-sizing: border-box;
	padding: 0 20px 35px;
	display: flex;
	flex-direction: column;
	margin-bottom: 10px;

	.preview {
		background-color: $background-color-price;
		width: 100%;
		height: 120px;
		margin: 0 0 18px;
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		.top {
			display: flex;
			justify-content: center;
			align-items: center;
			// color: var(--home-title-color);
			font-weight: 900;
			.monet {
				font-size: 25px;
				transform: translateY(5px);
				margin-right: 5px;
			}
			.amount {
				font-size: 55px;
			}
		}
		.bottom {
			position: relative;
			top: -10px;
			opacity: 0.6;
			font-weight: 900;
		}
	}
}
.group {
	// display: flex;
	align-items: center;
	justify-content: space-between;
	// background-color: $color-light-gray;
	margin-bottom: 5px;
	padding: 5px 10px;
	border-radius: 5px;
	border-bottom: 1px solid $color-light-gray;

	label {
		color: $color-fa;
		text-align: right;
		// width: 40px;
		margin-top: 5px;
		margin-right: 10px;
	}
}
.buttons-centered {
	margin-top: auto;
	flex-wrap: wrap;
}
.loading-wrapper {
	margin-top: 30px;
	img {
		margin: auto;
	}
}
.status-wrap,
.tooltipy {
	display: flex;
	justify-content: flex-start;
	// align-items: center;
	width: 100%;
	margin: 10px auto;
	line-height: 17px;

	.status {
		font-size: 15px;
		font-weight: 900;
		margin-right: 15px;
	}
}
.tooltipy {
	font-style: italic;
}
.rejected,
.approved,
.awaiting,
.cancelled {
	width: fit-content;
	padding: 3px 12px;
	font-weight: 700;
	border-radius: 6px;
	font-style: italic;
}
.rejected {
	background-color: $rejected-bg-color;
	color: $rejected-button-color;
	&::before {
		content: "Rejected";
	}
}
.cancelled {
	background-color: $rejected-bg-color;
	color: $rejected-button-color;
	&::before {
		content: "Cancelled";
	}
}
.approved {
	background-color: $approved-bg-color;
	color: $approved-button-color;
	&::before {
		content: "Approved";
	}
}
.awaiting {
	background-color: $awaiting-bg-color;
	color: $awaiting-button-color;
	&::before {
		content: "Awaiting approval";
	}
}
.error {
	width: 100%;
	padding-top: 15px;
	text-align: center;
	color: $red-cpl;
}
</style>
